<script setup lang="ts">
import {
  FEEDBACK_ATTACHMENT_MAX_BYTES,
  FEEDBACK_ATTACHMENT_MAX_COUNT,
  FEEDBACK_ATTACHMENT_TYPES,
  FEEDBACK_TYPE_LABELS,
  FeedbackType,
  type Feedback,
  type FeedbackType as FeedbackTypeValue,
} from '@cockpit/shared';
import { ref } from 'vue';
import { ApiError, api } from '../api';
import { queueFeedback } from '../offline/writes';
import { useOfflineStore } from '../stores/offline';
import { useToastStore } from '../stores/toast';
import ImageDropZone from './ImageDropZone.vue';

/**
 * Lot AQ : formulaire d'un retour (type, « Où ? », texte, captures), partagé par le panneau « Faire un retour » et
 * la page Retours. Il crée le retour puis envoie les captures une à une ; le parent garde le brouillon (v-model) et
 * le vide à l'événement `sent`. `idPrefix` distingue les deux formulaires quand ils sont affichés ensemble.
 */
const props = withDefaults(
  defineProps<{
    screens: readonly string[];
    idPrefix: string;
    /** Chemin de l'écran concerné, ajouté au contexte du retour. */
    contextRoute?: string;
  }>(),
  { contextRoute: undefined },
);
/** `complete` : false quand une capture au moins n'a pas pu être jointe (message déjà affiché). */
/** Lot AW : `queued` vrai quand le retour est gardé hors connexion (envoyé au retour du réseau). */
const emit = defineEmits<{ sent: [feedback: Feedback, complete: boolean, queued?: boolean] }>();

const type = defineModel<FeedbackTypeValue>('type', { required: true });
const screen = defineModel<string>('screen', { required: true });
const text = defineModel<string>('text', { required: true });
const files = defineModel<File[]>('files', { required: true });
const conversationId = defineModel<string | null>('conversationId', { required: true });
const observed = defineModel<string>('observed', { required: true });

const toast = useToastStore();
const offline = useOfflineStore();
const textArea = ref<HTMLTextAreaElement | null>(null);
const sending = ref(false);

function errorMessage(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Une erreur inattendue est survenue.';
}

async function submit(): Promise<void> {
  if (sending.value) return;
  if (!text.value.trim()) {
    toast.show("Décris en une phrase ce qui s'est passé.");
    textArea.value?.focus();
    return;
  }
  sending.value = true;
  const body = {
    type: type.value,
    screen: screen.value,
    text: text.value,
    ...(props.contextRoute ? { route: props.contextRoute } : {}),
    ...(conversationId.value ? { conversationId: conversationId.value } : {}),
    ...(conversationId.value && observed.value.trim() ? { observed: observed.value.trim() } : {}),
  };
  try {
    let created: Feedback;
    try {
      created = await api.createFeedback(body);
    } catch (err) {
      // Lot AW : sans réseau, le retour et ses captures sont gardés sur le téléphone.
      if (!(err instanceof ApiError) || err.status !== 0) throw err;
      await queueFeedback(body, files.value);
      offline.goneOffline();
      emit('sent', queuedFeedback(body.type, body.screen, body.text, files.value.length), true, true);
      return;
    }
    const failures: string[] = [];
    for (const file of files.value) {
      try {
        created = await api.uploadFeedbackAttachment(created.id, file);
      } catch (err) {
        failures.push(`« ${file.name} » : ${errorMessage(err)}`);
      }
    }
    if (failures.length > 0) {
      toast.show(
        failures.length === 1
          ? `Retour enregistré, mais une capture n'a pas pu être jointe. ${failures[0]}`
          : `Retour enregistré, mais ${failures.length} captures n'ont pas pu être jointes. ${failures.join(' ')}`,
        8000,
      );
    }
    emit('sent', created, failures.length === 0);
  } catch (err) {
    toast.show(errorMessage(err));
  } finally {
    sending.value = false;
  }
}

/** Retour affiché tel qu'il partira (gardé hors connexion). */
function queuedFeedback(t: FeedbackTypeValue, where: string, what: string, photos: number): Feedback {
  return {
    id: crypto.randomUUID(),
    type: t,
    screen: where,
    text: what,
    status: 'NEW',
    answer: null,
    attachments: Array.from({ length: photos }, () => ({
      id: crypto.randomUUID(),
      url: '',
      mime: 'image/png' as const,
    })),
    context: null,
    author: null,
    createdAt: new Date().toISOString(),
    updatedAt: null,
  };
}

defineExpose({
  focus: () => textArea.value?.focus(),
});
</script>

<template>
  <form class="sim feedback-form" :data-testid="`${idPrefix}-form`" @submit.prevent="submit">
    <div class="feedback-fields">
      <p v-if="conversationId" class="feedback-draft" data-testid="feedback-draft">
        Prérempli par l’assistant : relis, modifie si besoin, puis envoie. La conversation sera jointe au
        retour.
        <button class="linkbtn" type="button" @click="conversationId = null">Ne pas la joindre</button>
      </p>
      <label class="field" :for="`${idPrefix}Type`"
        >C'est…
        <select :id="`${idPrefix}Type`" v-model="type">
          <option v-for="value in FeedbackType.options" :key="value" :value="value">
            {{ FEEDBACK_TYPE_LABELS[value] }}
          </option>
        </select>
      </label>
      <label class="field" :for="`${idPrefix}Where`"
        ><span>Où ? <slot name="where-hint" /></span>
        <select :id="`${idPrefix}Where`" v-model="screen">
          <option v-for="label in screens" :key="label" :value="label">{{ label }}</option>
        </select>
      </label>
      <label class="field" :for="`${idPrefix}Text`"
        >Ce qui s'est passé
        <textarea
          :id="`${idPrefix}Text`"
          ref="textArea"
          v-model="text"
          style="min-height: 110px"
          maxlength="5000"
          placeholder="Ex. : j'ai validé la facture de Kiné Océane mais elle n'apparaît pas dans Axonaut."
        ></textarea>
      </label>
      <label v-if="conversationId" class="field" :for="`${idPrefix}Observed`"
        >Ce que l’assistant a constaté
        <textarea :id="`${idPrefix}Observed`" v-model="observed" rows="3" maxlength="2000"></textarea>
      </label>
      <div class="field" role="group" :aria-labelledby="`${idPrefix}FilesLabel`">
        <span :id="`${idPrefix}FilesLabel`">Captures d’écran (facultatif)</span>
        <ImageDropZone
          v-model="files"
          :max-count="FEEDBACK_ATTACHMENT_MAX_COUNT"
          :max-bytes="FEEDBACK_ATTACHMENT_MAX_BYTES"
          :types="FEEDBACK_ATTACHMENT_TYPES"
          :disabled="sending"
        />
      </div>
      <p class="muted" style="margin: 0; font-size: 12.5px">
        Ajoutés automatiquement : ton nom, l'écran, la date et les dernières actions que tu as validées.
      </p>
    </div>
    <div class="feedback-actions">
      <slot name="actions" :sending="sending">
        <button class="btn primary" type="submit" :disabled="sending">Envoyer le retour</button>
      </slot>
    </div>
  </form>
</template>
